import {
  HttpEvent,
  HttpEventType,
  HttpHandlerFn,
  HttpRequest,
  HttpResponse,
  HttpStatusCode,
  type HttpInterceptorFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { envs } from '@environments';
import { variables } from '@shared/helper/configs';
import { GeneralService } from '@shared/services/general.service';
import { TokenService } from '@shared/services/token.service';
import { BehaviorSubject, catchError, filter, map, switchMap, take, tap } from 'rxjs';

interface IErrorResponse {
  error: string;
  message: string;
  statusCode: number;
  operationName: string;
}

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<any>(null);
let hasDid = false;
const didSubject = new BehaviorSubject<any>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const generalService = inject(GeneralService);
  const tokenService = inject(TokenService);

  if (generalService.isBrowserOnly) {
    return next(req).pipe(
      tap((httpEvent: HttpEvent<any>) => {
        if (httpEvent instanceof HttpResponse) {
          const isp = httpEvent.headers.get('x-isp-name');
          if (isp) {
            generalService.ispName.set(isp.toLowerCase());
          }
          const countryCode = httpEvent.headers.get('x-country-code');
          if (countryCode) {
            generalService.countryCode.set(countryCode.toLowerCase());
          }
        }
      }),
      map((res) => {
        if (res.type === HttpEventType.Response) {
          if (res?.body) {
            const operationName = (req.body as any)?.operationName
              ? (req.body as any).operationName
              : '';
            const errors = (res.body as any).errors;
            if (errors?.length) {
              let presentedError: IErrorResponse;
              if (
                req.url === envs.authUrl ||
                req.url === envs.approveTvUrl ||
                req.url === envs.supportUrl
              ) {
                presentedError = {
                  ...errors[0],
                  operationName,
                };
              } else {
                presentedError = {
                  ...errors[0].extensions,
                  operationName,
                };
              }
              throw presentedError;
            }
          }
        }
        return res;
      }),
      catchError((error) => {
        if (error) {
          if (req.url.indexOf(envs.playPalUrl) > -1) {
            if (
              error.statusCode === HttpStatusCode.Unauthorized ||
              error.status === HttpStatusCode.Unauthorized
            ) {
              return handle401Error(req, next, tokenService);
            }
            throw error;
          } else if (req.url.indexOf(envs.viewCountUrl) > -1) {
            if (error.status === HttpStatusCode.Unauthorized) {
              return handle401Error(req, next, tokenService);
            }
            throw error;
          } else {
            if (error.statusCode === HttpStatusCode.Unauthorized) {
              if (error.operationName === 'refreshToken') {
                throw error;
              }
              return handle401Error(req, next, tokenService);
            } else if (error.statusCode === HttpStatusCode.PreconditionFailed) {
              return handle412Error(req, next, tokenService);
            }
          }
        }
        if (
          error?.status === HttpStatusCode.UnprocessableEntity ||
          error?.error?.statusCode === HttpStatusCode.UnprocessableEntity
        ) {
          localStorage.removeItem(variables.did);
          tokenService.logout().subscribe();
          throw error;
        }
        throw error;
      }),
    );
  }
  return next(req);
};

const handle401Error = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  tokenService: TokenService,
) => {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshTokenRequest = tokenService.refreshToken();
    return refreshTokenRequest.pipe(
      switchMap((res) => {
        if (res.data?.refreshToken?.statusCode === HttpStatusCode.Ok) {
          isRefreshing = false;
          refreshTokenSubject.next(tokenService.getAtk());
        }
        return next(request);
      }),
      catchError((e) => {
        isRefreshing = false;
        throw e;
      }),
    );
  }

  return refreshTokenSubject.pipe(
    filter((token) => token !== null),
    take(1),
    switchMap(() => next(request)),
  );
};

const handle412Error = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  tokenService: TokenService,
) => {
  if (!hasDid) {
    hasDid = true;
    didSubject.next(null);

    return tokenService.getDeviceId().pipe(
      switchMap((res) => {
        if (res.data && res.data.getDid?.statusCode === HttpStatusCode.Ok) {
          hasDid = false;
          didSubject.next(localStorage.getItem(variables.did));
          return next(request);
        } else {
          tokenService.logout().subscribe();
          return next(request);
        }
      }),
      catchError((err) => {
        hasDid = false;
        tokenService.logout().subscribe();
        throw err;
      }),
    );
  }

  return didSubject.pipe(
    filter((token) => token !== null),
    take(1),
    switchMap(() => next(request)),
  );
};
